import { ref } from 'vue';

const impresoraSeleccionada = ref(JSON.parse(localStorage.getItem('impresora_local') || 'null'));
const estado = ref('desconocido');
const listaImpresoras = ref([]);
const mostrarModal = ref(false);

const LOCAL_PRINTER_BASE = 'http://localhost:5000';

export function useImpresora() {
  
  const verificarEstadoServicio = async () => {
    try {
      const res = await fetch(`${LOCAL_PRINTER_BASE}/estado`);
      if (!res.ok) throw new Error('Error de red');
      const data = await res.json();
      return data.ok === true;
    } catch (error) {
      return false;
    }
  };

  const verificarConexion = async (silencioso = false) => {
    estado.value = 'verificando...';
    try {
      const servicioOk = await verificarEstadoServicio();
      if (!servicioOk) {
        estado.value = 'desconectado';
        if (!silencioso) mostrarModal.value = true;
        return false;
      }

      const res = await fetch(`${LOCAL_PRINTER_BASE}/api/detectar`);
      if (!res.ok) throw new Error('Error de red al detectar');
      const data = await res.json();
      
      if (data.conectado) {
        if (!impresoraSeleccionada.value) {
            estado.value = 'desconectado';
            if (!silencioso) mostrarModal.value = true;
            return false;
        }

        // Verificar si la impresora seleccionada sigue conectada físicamente
        const aunConectada = data.dispositivos.find(d => 
            String(d.vid) === String(impresoraSeleccionada.value.vid) && 
            String(d.pid) === String(impresoraSeleccionada.value.pid)
        );

        if (aunConectada) {
            estado.value = 'conectado';
            return true;
        } else {
            estado.value = 'desconectado';
            if (!silencioso) mostrarModal.value = true;
            return false;
        }
      } else {
        estado.value = 'desconectado';
        if (!silencioso) mostrarModal.value = true;
        return false;
      }
    } catch (error) {
      console.error("Error conectando con Servidor Local:", error);
      estado.value = 'desconectado';
      if (!silencioso) mostrarModal.value = true;
      return false;
    }
  };

  const buscarImpresoras = async () => {
    try {
      const res = await fetch(`${LOCAL_PRINTER_BASE}/api/detectar`);
      const data = await res.json();
      if (data.conectado && data.dispositivos) {
        listaImpresoras.value = data.dispositivos;
      } else {
        listaImpresoras.value = [];
      }
    } catch (error) {
      console.error("No se pudo conectar con el Servidor Local:", error);
      listaImpresoras.value = [];
    }
  };

  const guardarImpresora = async (impresoraObj) => {
    impresoraSeleccionada.value = impresoraObj;
    localStorage.setItem('impresora_local', JSON.stringify(impresoraObj));
    
    // Novedad: También le avisamos al programa Node local que guarde esta configuración
    try {
        await fetch(`${LOCAL_PRINTER_BASE}/api/configurar-impresora`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                vid: impresoraObj.vid,
                pid: impresoraObj.pid,
                encoding: 'CP858'
            })
        });
    } catch (error) {
        console.warn("Se guardó en local pero falló al avisar al servidor local");
    }

    estado.value = 'conectado';
    mostrarModal.value = false;
  };

  const imprimir = async (ticketData) => {
    // Verificamos de forma rápida si el servicio local está encendido
    const servicioOk = await verificarEstadoServicio();
    if (!servicioOk) {
      estado.value = 'desconectado';
      mostrarModal.value = true;
      return false;
    }

    try {
      ticketData.vid = impresoraSeleccionada.value?.vid;
      ticketData.pid = impresoraSeleccionada.value?.pid;

      const res = await fetch(`${LOCAL_PRINTER_BASE}/imprimir-ticket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ticketData)
      });
      
      if (!res.ok) {
          // Si el servidor local dice que no encontró la impresora USB (Error 503 o 400)
          estado.value = 'desconectado';
          mostrarModal.value = true;
          return false;
      }
      
      console.log("¡Impresión enviada al Servidor Local exitosamente!");
      return true;
    } catch (error) {
      console.error("Error al imprimir:", error);
      estado.value = 'desconectado';
      mostrarModal.value = true;
      return false;
    }
  };

  const probarImpresionBackend = async () => {
    try {
      // 1. Pedir a Laravel el Base64
      const baseUrl = process.env.VUE_APP_ROOT_API || 'http://127.0.0.1:8000/api';
      const token = localStorage.getItem('token');
      
      const resBackend = await fetch(`${baseUrl}/pos/impresion/test`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!resBackend.ok) {
        throw new Error("Error obteniendo ticket desde Laravel");
      }
      const dataBackend = await resBackend.json();
      
      if (!dataBackend.payload) {
        throw new Error("El backend no devolvió el payload Base64");
      }

      // 2. Mandarlo a Node.js
      const resNode = await fetch(`${LOCAL_PRINTER_BASE}/imprimir-base64`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payload: dataBackend.payload })
      });

      if (!resNode.ok) {
        throw new Error("Error enviando Base64 al servidor Node.js");
      }
      
      console.log("¡Prueba de impresión centralizada exitosa!");
      return true;
    } catch (error) {
      console.error("Error en la prueba de impresión:", error);
      return false;
    }
  };

  const imprimirTicketVentaBackend = async (ventaId) => {
    // Verificamos de forma rápida si el servicio local está encendido
    const servicioOk = await verificarEstadoServicio();
    if (!servicioOk) {
      estado.value = 'desconectado';
      mostrarModal.value = true;
      return false;
    }

    try {
      // 1. Pedir a Laravel el Base64
      const baseUrl = process.env.VUE_APP_ROOT_API || 'http://127.0.0.1:8000/api';
      const token = localStorage.getItem('token');
      
      const resBackend = await fetch(`${baseUrl}/pos/impresion/venta/${ventaId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (!resBackend.ok) {
        throw new Error("Error obteniendo ticket desde Laravel");
      }
      
      const dataBackend = await resBackend.json();
      
      if (!dataBackend.payload) {
        throw new Error("El backend no devolvió el payload Base64");
      }

      // 2. Mandarlo a Node.js
      const resNode = await fetch(`${LOCAL_PRINTER_BASE}/imprimir-base64`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            payload: dataBackend.payload,
            vid: impresoraSeleccionada.value?.vid,
            pid: impresoraSeleccionada.value?.pid
          })
      });

      if (!resNode.ok) {
        throw new Error("Error enviando Base64 al servidor Node.js");
      }
      
      console.log(`¡Impresión directa desde Backend exitosa para la venta ${ventaId}!`);
      return true;
    } catch (error) {
      console.error("Error al imprimir ticket backend:", error);
      estado.value = 'desconectado';
      mostrarModal.value = true;
      return false;
    }
  };

  // Etapa 3B: ticket armado en el frontend (ESC/POS en base64), mismo endpoint que el ticket del servidor
  const imprimirBase64Local = async (payloadBase64) => {
    const servicioOk = await verificarEstadoServicio();
    if (!servicioOk) {
      estado.value = 'desconectado';
      mostrarModal.value = true;
      return false;
    }
    try {
      const resNode = await fetch(`${LOCAL_PRINTER_BASE}/imprimir-base64`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: payloadBase64, vid: impresoraSeleccionada.value?.vid, pid: impresoraSeleccionada.value?.pid })
      });
      return resNode.ok;
    } catch (error) {
      console.error("Error al imprimir ticket local:", error);
      estado.value = 'desconectado';
      mostrarModal.value = true;
      return false;
    }
  };

  return {
    impresoraSeleccionada,
    estado,
    listaImpresoras,
    mostrarModal,
    verificarEstadoServicio,
    verificarConexion,
    buscarImpresoras,
    guardarImpresora,
    imprimir,
    imprimirTicketVentaBackend,
    imprimirBase64Local,
    probarImpresionBackend
  };
}
